import React from 'react';
import { Package, Wrench, Filter, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import useSEO from '../hooks/useSEO';

const Products = () => {
  const { t } = useLanguage();
  useSEO({
    title: 'Προϊόντα - Ανταλλακτικά & Εξοπλισμός Επαγγελματικής Κουζίνας | PrimeTech Santorini',
    description: 'Πώληση γνήσιων ανταλλακτικών για επαγγελματικές κουζίνες: εστίες, καυστήρες, θερμοστάτες, αντιστάσεις, απορροφητήρες, ανεμιστήρες, φίλτρα. Παράδοση στη Σαντορίνη.',
  });

  const categories = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: { el: 'Ανταλλακτικά', en: 'Spare Parts' },
      description: {
        el: 'Γνήσια ανταλλακτικά για όλα τα είδη εξοπλισμού',
        en: 'Genuine spare parts for all equipment types'
      },
      products: [
        { el: 'Εστίες & Καυστήρες', en: 'Burners & Stoves' },
        { el: 'Θερμοστάτες', en: 'Thermostats' },
        { el: 'Αντιστάσεις', en: 'Heating Elements' },
        { el: 'Φίλτρα', en: 'Filters' },
      ]
    },
    {
      icon: <Filter className="w-8 h-8" />,
      title: { el: 'Συστήματα Εξαερισμού', en: 'Ventilation Systems' },
      description: {
        el: 'Επαγγελματικός εξοπλισμός εξαερισμού',
        en: 'Professional ventilation equipment'
      },
      products: [
        { el: 'Απορροφητήρες', en: 'Extractors' },
        { el: 'Ανεμιστήρες', en: 'Fans' },
        { el: 'Φίλτρα Λίπους', en: 'Grease Filters' },
        { el: 'Αεραγωγοί', en: 'Ductwork' },
      ]
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-slate-900 py-24" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Package className="w-20 h-20 text-orange-500 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            {t({ el: 'ΠΡΟΪΟΝΤΑ', en: 'PRODUCTS' })}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {t({
              el: 'Πωλούμε εξειδικευμένο εξοπλισμό και ανταλλακτικά για επαγγελματικές κουζίνες',
              en: 'We sell specialized equipment and parts for commercial kitchens'
            })}
          </p>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <div
                key={index}
                data-testid={`product-category-${index}`}
                className="bg-white border border-slate-200 p-10 card-hover"
              >
                <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6 text-orange-600">
                  {category.icon}
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  {t(category.title)}
                </h2>
                <p className="text-slate-600 mb-6">
                  {t(category.description)}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {category.products.map((product, pIndex) => (
                    <li key={pIndex} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="text-slate-700">{t(product)}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/about"
                  data-testid={`product-inquiry-btn-${index}`}
                  className="text-orange-500 font-semibold hover:text-orange-600 transition-colors inline-flex items-center space-x-2"
                >
                  <span>{t({ el: 'ΖΗΤΗΣΤΕ ΠΡΟΣΦΟΡΑ', en: 'REQUEST QUOTE' })}</span>
                  <Phone className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6">
            {t({ el: 'ΠΑΡΑΓΓΕΛΙΕΣ & ΠΛΗΡΟΦΟΡΙΕΣ', en: 'ORDERS & INFORMATION' })}
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            {t({
              el: 'Για περισσότερες πληροφορίες σχετικά με τα προϊόντα μας, διαθεσιμότητα και τιμές, επικοινωνήστε μαζί μας άμεσα',
              en: 'For more information about our products, availability and pricing, contact us directly'
            })}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/chat"
              data-testid="products-chat-btn"
              className="bg-orange-500 text-white px-10 py-5 font-bold tracking-wider hover:bg-orange-600 transition-colors inline-flex items-center justify-center space-x-2"
            >
              <Phone className="w-6 h-6" />
              <span>{t({ el: 'ΕΠΙΚΟΙΝΩΝΗΣΤΕ ΜΑΖΙ ΜΑΣ', en: 'CONTACT US' })}</span>
            </Link>
            <Link
              to="/services"
              data-testid="products-services-btn"
              className="border-2 border-slate-900 text-slate-900 px-10 py-5 font-bold tracking-wider hover:bg-slate-100 transition-colors inline-flex items-center justify-center"
            >
              {t({ el: 'ΔΕΙΤΕ ΤΙΣ ΥΠΗΡΕΣΙΕΣ', en: 'VIEW SERVICES' })}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;